'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { ReactNode } from 'react'
import { useMemo, useRef, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import {
  Controller,
  useFormContext,
  type ControllerProps,
  type ValidationRule,
} from 'react-hook-form'

const DEBOUNCE_TIME = 600

function ruleValue<T extends boolean | number | string | RegExp>(
  rule: ValidationRule<T> | undefined,
): T | undefined {
  return rule && typeof rule === 'object' && 'value' in rule ? rule.value : rule
}

// FIXME: Reminder that validating multiple async fields is done serially not concurrently
// https://github.com/orgs/react-hook-form/discussions/5931

type RequiredRule = { required?: boolean }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncValidateRule = { asyncValidate?: (value: any) => Promise<string | undefined> }
type ValidationRules = StrictPick<
  Prettify<ControllerProps['rules'] & RequiredRule & AsyncValidateRule>,
  'max' | 'min' | 'maxLength' | 'minLength' | 'pattern' | 'required' | 'asyncValidate'
> & {
  /**
   * Custom validation function:
   * - `undefined` if validation passes
   * - An error message string if validation fails
   */
  validate?: Record<string, (value: string) => string | undefined>
}
type ValidationKeys = keyof (ValidationRules & RequiredRule)

// Generic types

/**
 * Defines props of the private UI component
 *
 * @example
 * ```tsx
 * type SomeUiInputProps = PrivateUiProps<string, {
 *  icon: React.ReactNode
 * }>
 * ```
 */
export type PrivateUiInputProps<
  ValueType extends string | boolean = string,
  OwnProps extends object = object,
> = {
  name: string
  label: ReactNode
  error?: { message?: string }
  isDisabled?: boolean
  isRequired?: boolean
  maxLength?: number
  isSubmitting: boolean
  isLoading: boolean
  isPending?: boolean
  value: ValueType
  onChange: (value: ValueType) => void
  onFocus?: () => void
} & OwnProps

/**
 * Defines props of the public component
 *
 * @example
 * ```tsx
 * type SomeInputProps = PublicInputProps<{
 *   icon: React.ReactNode
 * }, 'required' | 'asyncValidate'>
 * ```
 */
export type PublicInputProps<
  OwnProps extends object = object,
  BuiltInRules extends ValidationKeys = ValidationKeys,
> = {
  name: string
  label: ReactNode
  requiredErrorMessage?: string
} & OwnProps &
  StrictPick<ValidationRules, BuiltInRules> & { required?: boolean }

type AbstractInputProps = PublicInputProps<
  {
    render: (
      props: Parameters<ControllerProps['render']>[0] & {
        field: { disabled?: never; isDisabled?: boolean; isPending?: boolean }
        fieldState: { isRequired: boolean }
        fieldHelpers: {
          asyncValidate?: (value: string) => Promise<string | undefined> | string | undefined | void
        }
      },
    ) => React.ReactElement
  },
  'asyncValidate' | 'max' | 'maxLength' | 'min' | 'minLength' | 'pattern' | 'required' | 'validate'
>

/**
 * This is the component that will be used to create other inputs.
 * It holds the logic for validation and async validation.
 * It should NEVER be imported directly.
 */
export function AbstractInput({
  name,
  requiredErrorMessage,
  render,
  asyncValidate,
  validate,
  ...rules
}: StrictOmit<AbstractInputProps, 'label'>) {
  const [isValidating, setIsValidating] = useState(false)
  const form = useFormContext()
  const { control } = form
  const roundRef = useRef<number>(-1)
  const { t } = useTranslation(['AbstractInput'])

  const { required, max, min, maxLength, minLength, pattern } = (rules || {}) as ValidationRules

  const memoizedRules = useMemo(
    () => ({
      required,
      max,
      min,
      maxLength,
      minLength,
      pattern,
      validate: {
        ...validate,
        asyncValidate: asyncValidate
          ? async (value: string) => {
              roundRef.current += 1
              const latestRound = roundRef.current
              setIsValidating(true)
              await new Promise((resolve) => setTimeout(resolve, DEBOUNCE_TIME))
              const thisRound = roundRef.current

              if (thisRound !== latestRound) {
                return
              }

              const errorMessage = await asyncValidate?.(value)
              setIsValidating(false)

              return errorMessage
            }
          : () => undefined,
      },
    }),
    [required, max, min, maxLength, minLength, pattern, validate, asyncValidate],
  )

  return (
    <Controller
      control={control}
      name={name}
      rules={memoizedRules}
      render={(props) => {
        props.field.value = props.field.value || ''

        if (props.fieldState.error && !props.fieldState.error.message) {
          const fallbackMessages: Partial<Record<string, string>> = {
            required: requiredErrorMessage ?? t('AbstractInput:error.required'),
            minLength: t('AbstractInput:error.minLength', { minLength: ruleValue(minLength) }),
            maxLength: t('AbstractInput:error.maxLength', { maxLength: ruleValue(maxLength) }),
            min: t('AbstractInput:error.min', { min: ruleValue(min) }),
            max: t('AbstractInput:error.max', { max: ruleValue(max) }),
            pattern: t('AbstractInput:error.pattern'),
          }

          props.fieldState.error.message = fallbackMessages[props.fieldState.error.type]
        }

        props.fieldState = {
          ...props.fieldState,
          isValidating,
        }

        const { disabled, ...field } = props.field

        return render({
          ...props,
          field: { ...field, isDisabled: disabled },
          fieldState: { ...props.fieldState, isRequired: !!rules.required },
          fieldHelpers: { asyncValidate },
        })
      }}
    />
  )
}
